import React, { useEffect, useMemo, useState } from 'react';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';
import { FEATURES } from '../constants/features';
import FishIQSourceBadge from './FishIQSourceBadge';
import FishIQSpeciesImage from './FishIQSpeciesImage';
import { getFishIQDisplayImages, resolveFishIQSpeciesIdentity } from '../services/fishIQVisualReference';
import { buildFishIQSpeciesTacticalFusion } from '../lib/fishiq/fishIQMovementRead';

type DetailKind = 'species' | 'region';

interface FishIQDetailViewProps {
  isOpen?: boolean;
  kind?: DetailKind;
  id?: string | null;
  species?: any;
  region?: any;
  onClose?: () => void;
  catchLog?: any[];
  viewerUserId?: string | null;
  isSaved?: boolean;
  onToggleSaved?: () => void;
  onSelectSpecies?: (speciesName: string, payload?: any) => void;
  mapContext?: any | null;
  isDarkMode?: boolean;
  variant?: 'embedded' | 'modal' | string;
  maxBodyHeightClassName?: string;
}

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

const fishIQDetailMicroStyles = `
@keyframes roddoFishIQDetailMicroIn {
  from { opacity: 0; transform: translateY(5px) scale(0.996); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.roddo-fishiq-detail-micro-in {
  animation: roddoFishIQDetailMicroIn 190ms cubic-bezier(0.22, 1, 0.36, 1) both;
}
@media (prefers-reduced-motion: reduce) {
  .roddo-fishiq-detail-micro-in { animation: none !important; }
}
`;

const uniqueList = (input: any, limit = 8): string[] => {
  const src = Array.isArray(input)
    ? input
    : typeof input === 'string'
      ? input.split(/\n|,|•|·/g)
      : [];

  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of src) {
    const value = String(item ?? '').trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
    if (out.length >= limit) break;
  }
  return out;
};

const collectText = (...values: any[]): string[] => {
  const out: string[] = [];
  values.forEach((value) => {
    if (!value) return;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) out.push(trimmed);
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (typeof v === 'string' && v.trim()) out.push(v.trim());
      });
      return;
    }
    if (typeof value === 'object') {
      Object.values(value).forEach((v) => {
        if (typeof v === 'string' && v.trim()) out.push(v.trim());
      });
    }
  });
  return out;
};

const normalizeParagraphs = (values: string[], limit = 3): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    const trimmed = raw.replace(/\s+/g, ' ').trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
    if (out.length >= limit) break;
  }
  return out;
};

const speciesEmoji = (entity: any, habitats: string[]) => {
  const haystack = [
    entity?.commonName,
    entity?.name,
    entity?.scientificName,
    entity?.family,
    ...(Array.isArray(entity?.aliases) ? entity.aliases : []),
    ...habitats,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/(crab|lobster|shrimp|prawn|crawfish|crayfish)/.test(haystack)) return '🦀';
  if (/(clam|oyster|mussel|scallop|abalone)/.test(haystack)) return '🦪';
  if (/(squid|octopus|cephalopod)/.test(haystack)) return '🦑';
  if (/(urchin|sea urchin|uni)/.test(haystack)) return '🪸';
  if (/(trout|salmon|char|steelhead)/.test(haystack)) return '🐟';
  if (/(bass|walleye|perch|crappie|bluegill|sunfish|pike|muskie)/.test(haystack)) return '🐠';
  if (/(catfish|carp|sturgeon|drum|redfish|snook|tarpon)/.test(haystack)) return '🐡';
  if (/(tuna|mackerel|marlin|swordfish|mahi|kingfish)/.test(haystack)) return '🐟';
  if (/(flounder|halibut|sole)/.test(haystack)) return '🐠';
  return '🐟';
};

const inferConfidenceTone = (score: number | null) => {
  if (score == null) return { label: 'Signal-based', emoji: '✨' };
  if (score >= 85) return { label: 'Very high', emoji: '🎯' };
  if (score >= 70) return { label: 'High', emoji: '✅' };
  if (score >= 55) return { label: 'Moderate', emoji: '🧭' };
  return { label: 'Species profile', emoji: '🐟' };
};

const premiumChipStyle = (isDarkMode: boolean, tone: 'neutral' | 'water' | 'bait' | 'tech' | 'success') => {
  if (tone === 'water') {
    return {
      background: isDarkMode ? 'rgba(124,199,255,0.14)' : 'rgba(234,247,255,0.94)',
      color: isDarkMode ? '#DDF4FF' : '#1C6B94',
      borderColor: isDarkMode ? 'rgba(124,199,255,0.18)' : 'rgba(191,230,255,1)',
      boxShadow: isDarkMode
        ? '0 0 0 1px rgba(255,255,255,0.03), 0 8px 16px rgba(0,0,0,0.18)'
        : '0 1px 2px rgba(0,0,0,0.05), 0 8px 16px rgba(28,107,148,0.10)',
    };
  }
  if (tone === 'bait') {
    return {
      background: isDarkMode ? 'rgba(255,217,119,0.14)' : 'rgba(255,243,207,0.96)',
      color: isDarkMode ? '#FFE9B4' : '#7B5900',
      borderColor: isDarkMode ? 'rgba(255,217,119,0.18)' : 'rgba(241,215,143,1)',
      boxShadow: isDarkMode
        ? '0 0 0 1px rgba(255,255,255,0.03), 0 8px 16px rgba(0,0,0,0.18)'
        : '0 1px 2px rgba(0,0,0,0.05), 0 8px 16px rgba(123,89,0,0.10)',
    };
  }
  if (tone === 'tech') {
    return {
      background: isDarkMode ? 'rgba(138,112,242,0.11)' : 'rgba(47,128,237,0.07)',
      color: isDarkMode ? '#D8D0FF' : '#2469B2',
      borderColor: isDarkMode ? 'rgba(138,112,242,0.20)' : 'rgba(47,128,237,0.18)',
      boxShadow: isDarkMode
        ? '0 0 0 1px rgba(255,255,255,0.03), 0 8px 16px rgba(0,0,0,0.18)'
        : '0 1px 2px rgba(0,0,0,0.05), 0 8px 16px rgba(119,81,199,0.10)',
    };
  }
  if (tone === 'success') {
    return {
      background: isDarkMode ? 'rgba(122,232,184,0.14)' : 'rgba(234,255,245,0.96)',
      color: isDarkMode ? '#DFFFF1' : '#256A54',
      borderColor: isDarkMode ? 'rgba(122,232,184,0.18)' : 'rgba(172,235,204,1)',
      boxShadow: isDarkMode
        ? '0 0 0 1px rgba(255,255,255,0.03), 0 8px 16px rgba(0,0,0,0.18)'
        : '0 1px 2px rgba(0,0,0,0.05), 0 8px 16px rgba(37,106,84,0.10)',
    };
  }
  return {
    background: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.78)',
    color: isDarkMode ? '#EDF2FF' : '#6A5E52',
    borderColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(230,219,206,1)',
    boxShadow: isDarkMode
      ? '0 0 0 1px rgba(255,255,255,0.03), 0 8px 16px rgba(0,0,0,0.18)'
      : '0 1px 2px rgba(0,0,0,0.05), 0 8px 16px rgba(89,65,37,0.08)',
  };
};



const iconActionButtonClass =
  'grid h-9 w-9 shrink-0 place-items-center rounded-[14px] border text-base transition-all duration-200 ease-out hover:-translate-y-[1px] hover:scale-[1.02] active:translate-y-[1px] active:scale-[0.985] min-[390px]:h-10 min-[390px]:w-10 min-[390px]:rounded-[16px] min-[390px]:text-lg';

const sectionCardStyle = (isDarkMode: boolean) => ({
  background: isDarkMode
    ? '#101827'
    : '#FFFCF7',
  borderColor: isDarkMode ? 'rgba(167,228,255,0.09)' : 'rgba(152,128,244,0.12)',
  boxShadow: isDarkMode
    ? '0 6px 16px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.025)'
    : '0 14px 28px rgba(89,65,37,0.10), inset 0 1px 0 rgba(255,255,255,0.86)',
});

const MetricPill: React.FC<{
  label: string;
  value: string;
  isDarkMode: boolean;
  accent?: 'gold' | 'blue' | 'violet' | 'mint';
}> = ({ label, value, isDarkMode, accent = 'blue' }) => {
  const accentColor =
    accent === 'gold'
      ? isDarkMode
        ? 'rgba(255,220,136,0.20)'
        : 'rgba(255,242,205,0.96)'
      : accent === 'violet'
        ? isDarkMode
          ? 'rgba(190,152,255,0.18)'
          : 'rgba(243,238,255,0.96)'
        : accent === 'mint'
          ? isDarkMode
            ? 'rgba(122,232,184,0.18)'
            : 'rgba(234,255,245,0.96)'
          : isDarkMode
            ? 'rgba(124,199,255,0.16)'
            : 'rgba(234,247,255,0.96)';

  return (
    <div
      className="min-w-0 rounded-[18px] border px-3.5 py-3"
      style={{
        background: accentColor,
        borderColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(110,95,80,0.10)',
        boxShadow: isDarkMode
          ? 'inset 0 1px 0 rgba(255,255,255,0.04)'
          : 'inset 0 1px 0 rgba(255,255,255,0.72)',
      }}
    >
      <div
        className="text-[10px] font-black uppercase tracking-[0.14em]"
        style={{ color: isDarkMode ? 'rgba(228,236,255,0.72)' : '#85786A' }}
      >
        {label}
      </div>
      <div
        className="mt-1.5 truncate text-[14px] font-black uppercase tracking-[0.06em]"
        style={{ color: isDarkMode ? '#F8FAFF' : '#2F3F49' }}
      >
        {value}
      </div>
    </div>
  );
};

const CompactSection: React.FC<{
  title: string;
  badge?: string;
  defaultOpen?: boolean;
  isDarkMode: boolean;
  children: React.ReactNode;
}> = ({ title, badge, defaultOpen = false, isDarkMode, children }) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className="overflow-hidden rounded-[18px] border" style={sectionCardStyle(isDarkMode)}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left transition-colors duration-200 hover:bg-white/[0.03]"
        aria-expanded={open}
      >
        <div className="min-w-0">
          <div
            className="text-[12px] font-black uppercase tracking-[0.18em]"
            style={{ color: isDarkMode ? '#F8FBFF' : '#2B3655' }}
          >
            {title}
          </div>
          {badge ? (
            <div
              className="mt-1 text-[10px] font-black uppercase tracking-[0.14em]"
              style={{ color: isDarkMode ? '#66D9E8' : '#8D5BDA' }}
            >
              {badge}
            </div>
          ) : null}
        </div>

        <div
          className="grid h-10 w-10 shrink-0 place-items-center rounded-full border text-[18px] font-black transition-all duration-200 ease-out hover:-translate-y-[1px] hover:scale-[1.03] active:translate-y-[1px] active:scale-[0.985]"
          style={{
            background: isDarkMode
              ? '#0C1423'
              : '#F8F2E9',
            borderColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(124,96,255,0.16)',
            color: isDarkMode ? '#F7FAFF' : '#51463C',
            boxShadow: isDarkMode
              ? 'inset 0 1px 0 rgba(255,255,255,0.06)'
              : '0 6px 14px rgba(89,65,37,0.08), inset 0 1px 0 rgba(255,255,255,0.8)',
          }}
        >
          {open ? '−' : '+'}
        </div>
      </button>

      {open ? (
        <div
          className="border-t px-4 pb-4 pt-3.5"
          style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(79,53,31,0.08)' }}
        >
          {children}
        </div>
      ) : null}
    </section>
  );
};

const InfoBlock: React.FC<{
  title: string;
  tone?: 'water' | 'bait' | 'tech' | 'neutral';
  isDarkMode: boolean;
  children: React.ReactNode;
}> = ({ title, tone = 'neutral', isDarkMode, children }) => {
  const toneStyle = premiumChipStyle(isDarkMode, tone as any);
  return (
    <div
      className="rounded-[16px] border px-3.5 py-3.5"
      style={{
        background: isDarkMode ? '#0F1828' : 'rgba(255,255,255,0.60)',
        borderColor: isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(110,95,80,0.10)',
      }}
    >
      <div
        className="mb-2 inline-flex items-center rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.14em]"
        style={toneStyle}
      >
        {title}
      </div>
      <div className="text-[14px] leading-6" style={{ color: isDarkMode ? '#E8ECFF' : '#54483E' }}>
        {children}
      </div>
    </div>
  );
};

const BulletList: React.FC<{ items: string[]; isDarkMode: boolean }> = ({ items, isDarkMode }) => {
  if (!items.length) return null;
  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item} className="flex items-start gap-2.5">
          <span
            className="mt-[7px] block h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ background: isDarkMode ? '#9FE8FF' : '#6E63F6' }}
          />
          <p className="text-[14px] leading-6" style={{ color: isDarkMode ? '#E8ECFF' : '#54483E' }}>
            {item}
          </p>
        </div>
      ))}
    </div>
  );
};


const getDetailFishIQTimeWindow = (): 'morning' | 'day' | 'evening' | 'night' => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 11) return 'morning';
  if (hour >= 11 && hour < 17) return 'day';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
};

const prettyDetailWindow = (value: string) => {
  if (value === 'morning') return 'Morning';
  if (value === 'day') return 'Midday';
  if (value === 'evening') return 'Evening';
  if (value === 'night') return 'Night';
  return value;
};

const getNextDetailWindow = (current: string): 'morning' | 'day' | 'evening' | 'night' => {
  if (current === 'morning') return 'day';
  if (current === 'day') return 'evening';
  if (current === 'evening') return 'night';
  return 'morning';
};

const detailTimeHints = (items: string[]): string[] => {
  const out: string[] = [];
  for (const item of items) {
    const text = String(item || '').toLowerCase();
    if (/all day|anytime|open/.test(text)) out.push('morning', 'day', 'evening');
    if (/dawn|sunrise|morning|early/.test(text)) out.push('morning');
    if (/midday|noon|afternoon|day/.test(text)) out.push('day');
    if (/dusk|sunset|evening|golden/.test(text)) out.push('evening');
    if (/night|late|dark/.test(text)) out.push('night');
  }
  return Array.from(new Set(out));
};

const FishIQDetailView: React.FC<FishIQDetailViewProps> = ({
  kind = 'species',
  species,
  region,
  onClose,
  catchLog = [],
  isSaved,
  onToggleSaved,
  onSelectSpecies,
  mapContext = null,
  isDarkMode: isDarkModeProp,
  variant = 'embedded',
  maxBodyHeightClassName,
}) => {
  const isDarkMode = resolveRoddoDarkMode(isDarkModeProp);
  const ui = getRoddoUiTokens(isDarkMode);
  const entity = kind === 'region' ? region : species;
  const isModal = variant === 'modal';
  const showPublicSourceRegistry = kind === 'species' && Boolean((FEATURES as any).FISHIQ_PUBLIC_SOURCE_REGISTRY);
  const [expandedReferenceImage, setExpandedReferenceImage] = useState<any | null>(null);

  useEffect(() => {
    if (!expandedReferenceImage) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setExpandedReferenceImage(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [expandedReferenceImage]);

  const displayName =
    entity?.commonName || entity?.name || entity?.title || (kind === 'region' ? 'Region detail' : 'Species detail');
  const scientificName = String(entity?.scientificName || entity?.taxonomy?.scientificName || '').trim();
  const summary = String(entity?.summary || entity?.description || '').trim();
  const scoreRaw = Number(
    entity?.confidenceScore ?? entity?.confidence ?? entity?.score ?? entity?.matchConfidence ?? NaN,
  );
  const confidenceScore = Number.isFinite(scoreRaw) ? Math.round(scoreRaw) : null;
  const confidenceTone = inferConfidenceTone(confidenceScore);
  const aquaticKind = String(entity?.aquaticKind || (kind === 'species' ? 'finfish' : '')).toLowerCase();
  const aquaticKindLabel =
    aquaticKind === 'crustacean' ? 'Crustacean' :
    aquaticKind === 'mollusk' ? 'Mollusk' :
    aquaticKind === 'cephalopod' ? 'Cephalopod' :
    aquaticKind === 'echinoderm' ? 'Echinoderm' :
    aquaticKind === 'plant' ? 'Aquatic plant' :
    aquaticKind === 'finfish' ? 'Fish' : '';
  const cautionText = String(entity?.caution || '').trim();
  const harvestModes = uniqueList(entity?.harvestModes || [], 4).map((item) => item.replace(/_/g, ' '));

  const habitats = uniqueList(entity?.habitats || entity?.waterTypes || entity?.waterContext?.habitats || []);
  const waterNames = uniqueList([
    entity?.waterName,
    entity?.waterType,
    entity?.waterTypeKey,
    ...(Array.isArray(entity?.topWaterTypes) ? entity.topWaterTypes : []),
    ...(Array.isArray(entity?.waterContext?.waterTypes) ? entity.waterContext.waterTypes : []),
  ]);
  const bestBaits = uniqueList([
    entity?.__fishiq?.bestBait,
    entity?.__fishiq?.locationRank?.bestBait,
    ...(Array.isArray(entity?.tactics?.bestBaits) ? entity.tactics.bestBaits : []),
    ...(Array.isArray(entity?.bestBaits) ? entity.bestBaits : []),
    ...(Array.isArray(entity?.baits) ? entity.baits : []),
  ]);
  const bestTechniques = uniqueList(entity?.tactics?.bestTechniques || entity?.bestTechniques || entity?.techniques || []);
  const bestTimes = uniqueList(entity?.tactics?.bestTimesOfDay || entity?.bestTimesOfDay || [], 4);
  const behaviorTags = uniqueList(
    entity?.behaviorTags ||
      entity?.tactics?.behaviorTags ||
      entity?.patterns?.behaviorTags ||
      entity?.__fishiq?.behaviorTags ||
      entity?.behavior ||
      [],
    4,
  );
  const structureTags = uniqueList(
    entity?.structureTags ||
      entity?.tactics?.structureTags ||
      entity?.patterns?.structureTags ||
      entity?.__fishiq?.structureTags ||
      entity?.cover ||
      entity?.structure ||
      [],
    4,
  );
  const speciesKnowledge = entity?.speciesKnowledge || {};
  const knowledgeOverview = String(speciesKnowledge?.overview || summary || '').trim();
  const habitatFact = String(speciesKnowledge?.habitatFact || entity?.habitatFact || '').trim();
  const feedingFact = String(speciesKnowledge?.feedingFact || entity?.feedingProfile?.notes || entity?.feedingHabits || '').trim();
  const behaviorFact = String(speciesKnowledge?.behaviorFact || entity?.behaviorFact || '').trim();
  const structureFact = String(speciesKnowledge?.structureFact || entity?.structureFact || '').trim();
  const depthFact = String(speciesKnowledge?.depthFact || entity?.depthPreference || '').trim();
  const temperatureFact = String(speciesKnowledge?.temperatureFact || entity?.temperaturePreference || '').trim();
  const seasonalMovementFact = String(speciesKnowledge?.seasonalMovementFact || entity?.seasonalMovement || '').trim();
  const spawningFact = String(speciesKnowledge?.spawningFact || entity?.spawn?.notes || '').trim();
  const reproductionFact = String(speciesKnowledge?.reproductionFact || entity?.reproductionFact || '').trim();
  const timingFact = String(speciesKnowledge?.timingFact || '').trim();
  const tacticFact = String(speciesKnowledge?.tacticFact || '').trim();
  const knowledgeFieldNotes = uniqueList(speciesKnowledge?.fieldNotes || [], 5);
  const forageList = uniqueList(
    speciesKnowledge?.forage || entity?.feedingProfile?.primaryPrey || entity?.forage || entity?.prey || [],
    6,
  );
  const knowledgeHabitatTags = uniqueList(speciesKnowledge?.habitatTags || habitats, 8);
  const knowledgeStructureTags = uniqueList(speciesKnowledge?.structureTags || structureTags, 8);
  const knowledgeBehaviorTags = uniqueList(speciesKnowledge?.behaviorTags || behaviorTags, 8);
  const knowledgeSeasonTags = uniqueList(speciesKnowledge?.seasonTags || entity?.bestSeasons || [], 6);
  const knowledgeActivityWindows = uniqueList(speciesKnowledge?.activityWindows || entity?.activeTimes || entity?.bestTimesOfDay || [], 6);
  const speciesIcon = kind === 'region' ? '🗺️' : speciesEmoji(entity, habitats);
  const visualIdentity = resolveFishIQSpeciesIdentity(entity);
  const referenceImages =
    kind === 'species' && Boolean((FEATURES as any).FISHIQ_VISUAL_REFERENCES)
      ? getFishIQDisplayImages(visualIdentity.canonicalSpeciesId, visualIdentity.scientificName)
      : [];

  const areaRead = mapContext?.areaRead || null;
  const normalizeConfidenceIdentity = (value: any) =>
    String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  const predictionRows = Array.isArray(areaRead?.species) ? areaRead.species : [];
  const displayNameKey = normalizeConfidenceIdentity(displayName);
  const scientificNameKey = normalizeConfidenceIdentity(scientificName);
  const entityIdKey = normalizeConfidenceIdentity(entity?.id || entity?.slug || entity?.canonicalSpeciesId);
  const selectedPrediction =
    predictionRows.find((prediction: any) => {
      const predictionNameKey = normalizeConfidenceIdentity(prediction?.name);
      const predictionScientificKey = normalizeConfidenceIdentity(prediction?.scientificName);
      const predictionIdKey = normalizeConfidenceIdentity(prediction?.speciesId || prediction?.canonicalSpeciesId);
      return Boolean(
        (scientificNameKey && predictionScientificKey === scientificNameKey) ||
        (entityIdKey && predictionIdKey === entityIdKey) ||
        (displayNameKey && predictionNameKey === displayNameKey),
      );
    }) || null;
  const selectedConfidenceRaw = Number(selectedPrediction?.confidence);
  const selectedConfidenceScore = Number.isFinite(selectedConfidenceRaw)
    ? Math.max(0, Math.min(100, Math.round(selectedConfidenceRaw)))
    : null;
  const selectedConfidenceLabelRaw = String(selectedPrediction?.confidenceLabel || '').trim();
  const selectedConfidenceLabel = selectedConfidenceLabelRaw || (
    selectedConfidenceScore == null
      ? 'Limited local data'
      : selectedConfidenceScore >= 82
        ? 'Strong local match'
        : selectedConfidenceScore >= 66
          ? 'Likely match'
          : 'Limited local data'
  );
  const selectedConfidenceBreakdown = selectedPrediction?.confidenceBreakdown || null;
  const selectedConfidenceComponents = Array.isArray(selectedConfidenceBreakdown?.components)
    ? selectedConfidenceBreakdown.components.filter((component: any) => component?.available !== false)
    : [];
  const confidenceComponentRows = [
    ['regional', 'Regional fit'],
    ['habitat', 'Habitat fit'],
    ['behavior', 'Behavior fit'],
    ['environment', 'Live water'],
  ]
    .map(([key, label]) => {
      const component = selectedConfidenceComponents.find((row: any) => row?.key === key);
      const score = Number(component?.score);
      return component && Number.isFinite(score)
        ? { key, label, score: Math.max(0, Math.min(100, Math.round(score))) }
        : null;
    })
    .filter(Boolean) as Array<{ key: string; label: string; score: number }>;
  const confidenceCap = String(selectedConfidenceBreakdown?.cappedBy || '').trim();
  const localConfidenceComponent = selectedConfidenceComponents.find((row: any) => row?.key === 'local') || null;
  const localConfidenceScore = Number(localConfidenceComponent?.score);
  const tacticalFusion = buildFishIQSpeciesTacticalFusion(
    areaRead?.tacticalFusion || null,
    entity,
  );
  const liveTacticalActive = Boolean(tacticalFusion?.active);
  const tacticalConfidence = Math.max(
    0,
    Math.min(100, Math.round(Number(tacticalFusion?.confidence || 0) * 100)),
  );
  const tacticalFacts = uniqueList(tacticalFusion?.facts || [], 4);
  const tacticalWhy = uniqueList(tacticalFusion?.why || [], 4);

  const overviewParagraphs = useMemo(
    () =>
      normalizeParagraphs(
        collectText(
          knowledgeOverview,
          habitatFact,
          feedingFact,
          behaviorFact,
          summary,
          entity?.overview,
          entity?.descriptionShort,
        ),
        3,
      ),
    [behaviorFact, entity, feedingFact, habitatFact, knowledgeOverview, summary],
  );

  const confidenceParagraphs = useMemo(
    () =>
      normalizeParagraphs(
        collectText(
          entity?.confidenceExplanation,
          entity?.confidenceReason,
          entity?.matchReason,
          entity?.reasoning,
          entity?.explanation,
          entity?.sourceTitle,
        ),
        3,
      ),
    [entity],
  );

  const waterParagraphs = useMemo(
    () =>
      normalizeParagraphs(
        collectText(
          entity?.waterContext?.summary,
          entity?.waterContext?.notes,
          entity?.seasonality,
          entity?.conditions,
          entity?.locationHint,
        ),
        3,
      ),
    [entity],
  );

  const loggedCount = useMemo(() => {
    const injected = Number(entity?.viewerCatchCount ?? 0);
    if (Number.isFinite(injected) && injected > 0) return injected;
    const normalized = String(displayName || '').trim().toLowerCase();
    if (!normalized || !Array.isArray(catchLog)) return 0;
    return catchLog.filter((entry: any) => String(entry?.species || '').trim().toLowerCase() === normalized).length;
  }, [catchLog, displayName, entity?.viewerCatchCount]);

  const heroChips = [
    aquaticKindLabel ? { label: aquaticKindLabel, emoji: aquaticKind === 'finfish' ? '🐟' : '🦀', tone: 'tech' as const } : null,
    habitats[0] ? { label: habitats[0], emoji: '🌊', tone: 'water' as const } : null,
    waterNames[0] ? { label: waterNames[0], emoji: '💧', tone: 'water' as const } : null,
    bestTechniques[0] ? { label: bestTechniques[0], emoji: '🧠', tone: 'tech' as const } : null,
    confidenceScore != null && confidenceScore >= 85
      ? { label: 'Strong match', emoji: '🎯', tone: 'success' as const }
      : null,
  ].filter(Boolean) as Array<{ label: string; emoji: string; tone: 'water' | 'tech' | 'success' }>;

  const modalFrameClass = isModal ? 'rounded-t-[34px] rounded-b-[28px] sm:rounded-[40px]' : 'rounded-[16px]';
  const heroPaddingClass = isModal ? 'px-5 py-5 sm:px-7 sm:py-6' : 'px-4 py-4 sm:px-5';
  const bodyPaddingClass = isModal ? 'px-5 pb-5 pt-4 sm:px-7 sm:pb-6' : 'px-4 pb-4 pt-3 sm:px-5';

  const profileBestBet =
    bestTechniques[0] && bestTimes[0]
      ? `${bestTechniques[0]} around ${bestTimes[0]}`
      : bestTechniques[0]
        ? bestTechniques[0]
        : bestBaits[0]
          ? `Work ${bestBaits[0]}`
          : waterNames[0] || habitats[0] || 'Follow the strongest signal';

  const bestBet = liveTacticalActive
    ? String(tacticalFusion?.bestBet || tacticalFusion?.approach || profileBestBet)
    : profileBestBet;

  const patternRows = [
    bestBaits[0] ? `Best bait look: ${bestBaits.slice(0, 3).join(', ')}` : '',
    bestTechniques[0] ? `Best presentation: ${bestTechniques.slice(0, 3).join(', ')}` : '',
    bestTimes[0] ? `Most promising timing: ${bestTimes.slice(0, 3).join(', ')}` : '',
    ...(uniqueList(entity?.__fishiq?.supportingReads || [], 3).map((item) => `Supporting read: ${item}`)),
].filter(Boolean);

  const quickNotes = normalizeParagraphs(
    collectText(
      entity?.__fishiq?.localReadBody,
      entity?.feedingHabits,
      entity?.diet,
      entity?.forage,
      entity?.prey,
      entity?.bestBet,
      entity?.behavior,
    ),
    3,
  );


  const detailActionPlan = useMemo(() => {
    const current = getDetailFishIQTimeWindow();
    const next = getNextDetailWindow(current);
    const hints = detailTimeHints(bestTimes);
    const activeNow = hints.includes(current);
    const betterLater = !activeNow && hints.length > 0;
    const water = String(mapContext?.waterName || waterNames[0] || habitats[0] || '').trim();
    const tacticalWhere = liveTacticalActive ? String(tacticalFusion?.where || '').trim() : '';
    const tacticalWhen = liveTacticalActive ? String(tacticalFusion?.when || '').trim() : '';
    const tacticalApproach = liveTacticalActive ? String(tacticalFusion?.approach || '').trim() : '';
    const technique = bestTechniques[0] || 'slow, intentional casts';
    const bait = bestBaits[0] || 'a natural presentation';
    const cover = water || 'visible cover, edges, shade, or structure';
    const confidence = liveTacticalActive
      ? `${tacticalConfidence}% live-water fusion`
      : activeNow
      ? 'moderate-to-strong'
      : betterLater
        ? `light now, better near ${prettyDetailWindow(next).toLowerCase()}`
        : loggedCount > 0
          ? 'memory-backed but timing-light'
          : 'species-profile based';

    const bestNow = tacticalWhen || (activeNow
      ? `${prettyDetailWindow(current)} lines up. Start with ${cover}.`
      : betterLater
        ? `Use this as the next practical window: ${prettyDetailWindow(next).toLowerCase()}.`
        : `Start with ${cover}; match the species habitat before changing presentations.`);
    const whyHere = tacticalWhere || [
      structureTags[0],
      behaviorTags[0],
      water ? `${water} profile` : '',
    ].filter(Boolean).join(' • ') || `${cover} matches this read`;

    return {
      activeNow,
      label: liveTacticalActive ? 'Live water' : activeNow ? 'Active now' : betterLater ? `Better near ${prettyDetailWindow(next)}` : 'Light right now',
      confidence,
      rows: [
        `Timing: ${bestNow}`,
        `Habitat: ${whyHere}.`,
        `Approach: ${tacticalApproach || `${technique}${bait ? ` with ${bait}` : ''}`}.`,
        `Field note: cover the highest-percentage structure first, then adjust depth or retrieve speed.`,
      ],
    };
  }, [behaviorTags, bestBaits, bestTechniques, bestTimes, habitats, liveTacticalActive, loggedCount, mapContext, structureTags, tacticalConfidence, tacticalFusion, waterNames]);

  const pocketSummary = `${displayName} profile: ${(waterNames[0] || habitats[0] || 'local water').toString().toLowerCase()}, ${(structureTags[0] || behaviorTags[0] || 'edges and cover').toString().toLowerCase()}. Try ${(bestTechniques[0] || 'a clean first pattern').toString().toLowerCase()}${bestBaits[0] ? ` with ${bestBaits[0]}` : ''}.`;

  return (
    <>
      <style>{fishIQDetailMicroStyles}</style>
    <div
      className={cx('roddo-fishiq-detail-micro-in relative flex h-full min-h-0 flex-col overflow-hidden border', modalFrameClass)}
      style={{
        background: isDarkMode
          ? '#090D18'
          : '#F7F2E8',
        borderColor: ui.borderStrong,
        boxShadow: isDarkMode
          ? '0 18px 42px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.05)'
          : '0 14px 30px rgba(89,65,37,0.11), inset 0 1px 0 rgba(255,255,255,0.74)',
      }}
    >
      {isModal ? (
        <div className="relative z-20 flex shrink-0 justify-center pt-3">
          <div
            className="h-1.5 w-14 rounded-full"
            style={{ background: isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(80,62,45,0.16)' }}
          />
        </div>
      ) : null}

      <div
        className={cx('relative z-10 shrink-0 border-b', isModal ? 'px-4 pb-4 pt-3 min-[390px]:px-5' : 'px-4 py-4 min-[390px]:px-5')}
        style={{ borderColor: ui.border }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div
              className="text-[10px] font-black uppercase tracking-[0.18em]"
              style={{ color: isDarkMode ? '#66D9E8' : '#1D6D96' }}
            >
              {kind === 'species' ? 'Roddo species guide' : 'Roddo region guide'}
            </div>
            <h2
              className="mt-1 truncate text-[23px] font-black leading-tight min-[390px]:text-[27px]"
              style={{ color: ui.textPrimary }}
            >
              {displayName}
            </h2>
            {scientificName ? (
              <div
                className="mt-1 truncate text-[12px] italic min-[390px]:text-[13px]"
                style={{ color: ui.textMuted }}
              >
                {scientificName}
              </div>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {typeof onToggleSaved === 'function' ? (
              <button
                type="button"
                onClick={onToggleSaved}
                className={iconActionButtonClass}
                style={{
                  background: isSaved
                    ? isDarkMode ? 'rgba(242,184,75,0.16)' : '#FFF2C8'
                    : isDarkMode ? '#151F31' : 'rgba(255,255,255,0.82)',
                  color: isSaved ? (isDarkMode ? '#FFE39B' : '#7A5A00') : ui.textPrimary,
                  borderColor: ui.border,
                }}
                aria-label={isSaved ? 'Unsave species' : 'Save species'}
                title={isSaved ? 'Saved' : 'Save'}
              >
                {isSaved ? '★' : '☆'}
              </button>
            ) : null}

            {typeof onClose === 'function' ? (
              <button
                type="button"
                onClick={onClose}
                className={`${iconActionButtonClass} text-[18px]`}
                style={{
                  background: isDarkMode ? '#151F31' : 'rgba(255,255,255,0.82)',
                  color: ui.textPrimary,
                  borderColor: ui.border,
                }}
                aria-label="Close water read detail"
                title="Close"
              >
                ×
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className={cx('relative z-10 flex min-h-0 flex-1 flex-col', bodyPaddingClass)}>
        <div
          className={cx('min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1', maxBodyHeightClassName)}
          style={{
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            scrollbarGutter: 'stable',
          }}
        >
          <div className="space-y-3 pb-6">
            <section
              className="overflow-hidden rounded-[20px] border"
              style={{
                background: isDarkMode ? '#101827' : 'rgba(255,250,243,0.96)',
                borderColor: ui.border,
                boxShadow: isDarkMode
                  ? '0 5px 14px rgba(0,0,0,0.13)'
                  : '0 12px 24px rgba(89,65,37,0.08)',
              }}
            >
              <button
                type="button"
                disabled={!referenceImages[0]}
                onClick={() => referenceImages[0] && setExpandedReferenceImage(referenceImages[0])}
                className="group relative block w-full text-left disabled:cursor-default"
                aria-label={referenceImages[0] ? `Open full-size ${displayName} image` : undefined}
              >
                <FishIQSpeciesImage
                  species={entity}
                  image={referenceImages[0] || undefined}
                  fallback={<span aria-hidden="true" className="text-[72px]">{speciesIcon}</span>}
                  size="hero"
                  className="aspect-[16/9] w-full bg-black/10"
                  imageClassName="object-cover transition-transform duration-300 group-hover:scale-[1.01]"
                  isDarkMode={isDarkMode}
                />
                {referenceImages[0] ? (
                  <span
                    className="pointer-events-none absolute bottom-3 right-3 grid h-9 w-9 place-items-center rounded-full border text-[15px] backdrop-blur-md"
                    style={{
                      background: isDarkMode ? 'rgba(9,13,24,0.76)' : 'rgba(255,255,255,0.88)',
                      borderColor: isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(70,60,50,0.12)',
                      color: isDarkMode ? '#F4F6FA' : '#3D342D',
                    }}
                    aria-hidden="true"
                  >
                    ↗
                  </span>
                ) : null}
              </button>

              <div className="px-4 py-4 min-[390px]:px-5">
                <div
                  className="text-[10px] font-black uppercase tracking-[0.17em]"
                  style={{ color: isDarkMode ? '#66D9E8' : '#1D6D96' }}
                >
                  Summary
                </div>
                <p
                  className="mt-2 text-[14px] font-semibold leading-6"
                  style={{ color: isDarkMode ? '#DDE4EF' : '#5B5148' }}
                >
                  {knowledgeOverview || summary || overviewParagraphs[0] || pocketSummary}
                </p>
              </div>
            </section>

            {kind === 'species' && selectedConfidenceScore != null ? (
              <section
                className="overflow-hidden rounded-[20px] border px-4 py-4 min-[390px]:px-5"
                style={{
                  background: isDarkMode ? '#101827' : 'rgba(255,250,243,0.96)',
                  borderColor: isDarkMode ? 'rgba(169,154,248,0.18)' : 'rgba(138,112,242,0.16)',
                  boxShadow: isDarkMode ? '0 8px 20px rgba(0,0,0,0.14)' : '0 10px 22px rgba(89,65,37,0.07)',
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div
                      className="text-[10px] font-black uppercase tracking-[0.17em]"
                      style={{ color: isDarkMode ? '#A99AF8' : '#7158D6' }}
                    >
                      Read confidence
                    </div>
                    <div className="mt-1 text-[18px] font-black" style={{ color: ui.textPrimary }}>
                      {selectedConfidenceLabel || 'Evidence-weighted fit'}
                    </div>
                  </div>
                  <div
                    className="rounded-full border px-3 py-1.5 text-[11px] font-black"
                    style={{
                      background:
                        selectedConfidenceScore >= 82
                          ? isDarkMode ? 'rgba(122,232,184,0.14)' : 'rgba(234,255,245,0.96)'
                          : selectedConfidenceScore >= 66
                            ? isDarkMode ? 'rgba(102,217,232,0.11)' : 'rgba(230,248,252,0.96)'
                            : isDarkMode ? 'rgba(242,184,75,0.10)' : 'rgba(255,248,221,0.90)',
                      borderColor: ui.border,
                      color: ui.textPrimary,
                    }}
                  >
                    {selectedConfidenceScore}%
                  </div>
                </div>

                {confidenceComponentRows.length ? (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {confidenceComponentRows.map((component) => (
                      <div
                        key={component.key}
                        className="rounded-[14px] border px-3 py-2.5"
                        style={{
                          background: isDarkMode ? '#0A1120' : 'rgba(255,255,255,0.64)',
                          borderColor: ui.border,
                        }}
                      >
                        <div className="text-[9px] font-black uppercase tracking-[0.12em]" style={{ color: ui.textMuted }}>
                          {component.label}
                        </div>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <span className="text-[12px] font-black" style={{ color: ui.textPrimary }}>
                            {component.score >= 82 ? 'Strong' : component.score >= 66 ? 'Good' : component.score >= 48 ? 'Possible' : 'Limited'}
                          </span>
                          <span className="text-[11px] font-black" style={{ color: isDarkMode ? '#A99AF8' : '#7158D6' }}>
                            {component.score}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

                <div className="mt-3 text-[11px] font-semibold leading-5" style={{ color: ui.textSecondary }}>
                  {confidenceCap
                    ? `Confidence limited by: ${confidenceCap}.`
                    : Number.isFinite(localConfidenceScore) && localConfidenceScore > 28
                      ? `Local evidence is contributing to this read (${Math.round(localConfidenceScore)}%).`
                      : 'This score reflects regional fit, habitat, behavior, live-water quality, and source evidence.'}
                </div>
              </section>
            ) : null}

            {kind === 'species' && liveTacticalActive ? (
              <section
                className="overflow-hidden rounded-[20px] border px-4 py-4 min-[390px]:px-5"
                style={{
                  background: isDarkMode ? 'rgba(35,29,68,0.72)' : 'rgba(246,242,255,0.96)',
                  borderColor: isDarkMode ? 'rgba(169,154,248,0.22)' : 'rgba(138,112,242,0.18)',
                  boxShadow: isDarkMode ? '0 10px 24px rgba(0,0,0,0.18)' : '0 10px 22px rgba(89,65,37,0.07)',
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div
                      className="text-[10px] font-black uppercase tracking-[0.17em]"
                      style={{ color: isDarkMode ? '#A99AF8' : '#7158D6' }}
                    >
                      Right now
                    </div>
                    <div className="mt-1 text-[17px] font-black leading-6" style={{ color: ui.textPrimary }}>
                      {tacticalFusion?.rightNow || 'Live environmental read'}
                    </div>
                  </div>
                  <span
                    className="shrink-0 rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.12em]"
                    style={premiumChipStyle(isDarkMode, 'tech')}
                  >
                    Live water{tacticalConfidence > 0 ? ` · ${tacticalConfidence}%` : ''}
                  </span>
                </div>

                {tacticalFacts.length ? (
                  <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5">
                    <span
                      className="text-[9px] font-black uppercase tracking-[0.14em]"
                      style={{ color: isDarkMode ? '#66D9E8' : '#1D6D96' }}
                    >
                      Water facts
                    </span>
                    <span className="text-[10.5px] font-bold leading-5" style={{ color: ui.textSecondary }}>
                      {tacticalFacts.join(' • ')}
                    </span>
                  </div>
                ) : null}

                {tacticalWhy.length ? (
                  <div className="mt-3 rounded-[14px] border px-3 py-3" style={{ background: isDarkMode ? 'rgba(10,17,32,0.70)' : 'rgba(255,255,255,0.62)', borderColor: ui.border }}>
                    <div className="text-[9px] font-black uppercase tracking-[0.14em]" style={{ color: isDarkMode ? '#F2B84B' : '#8A6200' }}>
                      Why this changed
                    </div>
                    <div className="mt-2 space-y-1.5">
                      {tacticalWhy.map((reason) => (
                        <div key={reason} className="flex items-start gap-2 text-[11.5px] font-semibold leading-5" style={{ color: ui.textSecondary }}>
                          <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: isDarkMode ? '#A99AF8' : '#7158D6' }} />
                          <span>{reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </section>
            ) : null}

            {kind === 'species' ? (
              <section className="overflow-hidden rounded-[20px] border px-4 py-4 min-[390px]:px-5" style={sectionCardStyle(isDarkMode)}>
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <div
                      className="text-[10px] font-black uppercase tracking-[0.17em]"
                      style={{ color: isDarkMode ? '#66D9E8' : '#1D6D96' }}
                    >
                      Quick plan
                    </div>
                    <div className="mt-1 text-[18px] font-black" style={{ color: ui.textPrimary }}>
                      Start simple
                    </div>
                  </div>
                  <div
                    className="rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.12em]"
                    style={premiumChipStyle(isDarkMode, detailActionPlan.activeNow ? 'success' : 'neutral')}
                  >
                    {detailActionPlan.label}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  {[
                    ['Where', liveTacticalActive ? tacticalFusion?.where : (mapContext?.waterName || waterNames[0] || habitats[0] || 'Best available habitat'), 'water'],
                    ['When', liveTacticalActive ? tacticalFusion?.when : (bestTimes[0] || detailActionPlan.label), 'tech'],
                    ['Throw', bestBaits[0] || 'Match local forage', 'bait'],
                    ['Approach', liveTacticalActive ? tacticalFusion?.approach : (bestTechniques[0] || structureTags[0] || 'Work high-percentage cover'), 'neutral'],
                  ].map(([label, value, tone]) => (
                    <div
                      key={String(label)}
                      className="min-w-0 rounded-[15px] border px-3 py-3"
                      style={{
                        background: isDarkMode ? '#0A1120' : 'rgba(255,255,255,0.68)',
                        borderColor: ui.border,
                      }}
                    >
                      <div
                        className="text-[9px] font-black uppercase tracking-[0.14em]"
                        style={{
                          color:
                            tone === 'bait' ? (isDarkMode ? '#F2B84B' : '#8A6200') :
                            tone === 'water' ? (isDarkMode ? '#66D9E8' : '#1D6D96') :
                            tone === 'tech' ? (isDarkMode ? '#A99AF8' : '#8A70F2') : ui.textMuted,
                        }}
                      >
                        {label}
                      </div>
                      <div
                        className="mt-1 line-clamp-2 text-[12px] font-black leading-5"
                        style={{ color: ui.textPrimary }}
                      >
                        {String(value)}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {kind === 'species' ? (
              <section
                className="overflow-hidden rounded-[20px] border px-4 py-4 min-[390px]:px-5"
                style={{
                  background: isDarkMode ? '#101827' : 'rgba(255,250,243,0.96)',
                  borderColor: isDarkMode ? 'rgba(102,217,232,0.18)' : 'rgba(29,109,150,0.16)',
                  boxShadow: isDarkMode ? '0 14px 30px rgba(0,0,0,0.18)' : '0 10px 22px rgba(89,65,37,0.07)',
                }}
              >
                <div className="text-[10px] font-black uppercase tracking-[0.17em]" style={{ color: isDarkMode ? '#66D9E8' : '#1D6D96' }}>
                  Roddo recommendation
                </div>
                <div className="mt-2 text-[19px] font-black leading-6" style={{ color: ui.textPrimary }}>
                  {bestBet}
                </div>
                <p className="mt-2 text-[13px] font-semibold leading-6" style={{ color: ui.textSecondary }}>
                  {quickNotes[0] || detailActionPlan.rows[2] || detailActionPlan.rows[0]}
                </p>
                <div className="mt-3 rounded-[14px] border px-3 py-3" style={{ background: isDarkMode ? '#0A1120' : 'rgba(255,255,255,0.62)', borderColor: ui.border }}>
                  <div className="text-[9px] font-black uppercase tracking-[0.14em]" style={{ color: isDarkMode ? '#F2B84B' : '#8A6200' }}>
                    Why
                  </div>
                  <div className="mt-1 text-[12px] font-semibold leading-5" style={{ color: ui.textSecondary }}>
                    {liveTacticalActive && tacticalWhy.length ? tacticalWhy.slice(0, 2).join(' • ') : (detailActionPlan.rows[1] || pocketSummary)}
                  </div>
                </div>

                {typeof onSelectSpecies === 'function' ? (
                  <button
                    type="button"
                    onClick={() => onSelectSpecies(String(displayName), {
                      species: String(displayName),
                      prefilledData: {
                        species: String(displayName),
                        notes: `${detailActionPlan.label}. ${detailActionPlan.rows.join(' ')}`,
                      },
                    })}
                    className="mt-4 w-full rounded-[14px] border px-4 py-3.5 text-center text-[12px] font-black uppercase tracking-[0.15em] text-white transition active:scale-[0.99]"
                    style={{
                      background: isDarkMode ? '#8A70F2' : '#2F80ED',
                      borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(50,73,128,0.18)',
                      boxShadow: isDarkMode ? '0 12px 24px rgba(34,23,99,0.30)' : '0 10px 20px rgba(35,52,91,0.18)',
                    }}
                  >
                    Log this fish
                  </button>
                ) : null}
              </section>
            ) : null}

            <div className="pt-1">
              <div className="px-1 text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: ui.textMuted }}>
                {kind === 'species' ? 'Species guide' : 'Regional guide'}
              </div>
            </div>

            <CompactSection
              title={kind === 'species' ? 'Overview' : 'Regional overview'}
              badge={habitats[0] || waterNames[0] || 'Profile'}
              defaultOpen={false}
              isDarkMode={isDarkMode}
            >
              <div className="space-y-3 text-[14px] leading-6" style={{ color: ui.textSecondary }}>
                {overviewParagraphs.length
                  ? overviewParagraphs.map((paragraph, index) => <p key={`${paragraph}-${index}`}>{paragraph}</p>)
                  : <p>{pocketSummary}</p>}

                {(habitatFact || behaviorFact || depthFact || temperatureFact) ? (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {habitatFact ? <InfoBlock title="Habitat" tone="water" isDarkMode={isDarkMode}>{habitatFact}</InfoBlock> : null}
                    {behaviorFact ? <InfoBlock title="Behavior" tone="tech" isDarkMode={isDarkMode}>{behaviorFact}</InfoBlock> : null}
                    {depthFact ? <InfoBlock title="Depth" tone="neutral" isDarkMode={isDarkMode}>{depthFact}</InfoBlock> : null}
                    {temperatureFact ? <InfoBlock title="Temperature" tone="neutral" isDarkMode={isDarkMode}>{temperatureFact}</InfoBlock> : null}
                  </div>
                ) : null}
              </div>
            </CompactSection>

            {kind === 'species' ? (
              <CompactSection
                title="Habitat + behavior"
                badge={structureTags[0] || behaviorTags[0] || 'Where to look'}
                isDarkMode={isDarkMode}
              >
                <div className="space-y-3">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <InfoBlock title="Where to look" tone="water" isDarkMode={isDarkMode}>
                      {[waterNames[0], habitats[0], structureTags[0]].filter(Boolean).join(' • ') || 'Start with the strongest known habitat and structure.'}
                    </InfoBlock>
                    <InfoBlock title="How it behaves" tone="tech" isDarkMode={isDarkMode}>
                      {behaviorFact || behaviorTags.slice(0, 3).join(' • ') || 'Use the species behavior profile to decide how aggressively to cover water.'}
                    </InfoBlock>
                  </div>
                  {knowledgeStructureTags.length || knowledgeBehaviorTags.length ? (
                    <div className="flex flex-wrap gap-2">
                      {[...knowledgeStructureTags.slice(0, 4), ...knowledgeBehaviorTags.slice(0, 3)].map((item) => (
                        <span key={item} className="rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.10em]" style={premiumChipStyle(isDarkMode, 'neutral')}>
                          {item}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </CompactSection>
            ) : null}

            {kind === 'species' ? (
              <CompactSection
                title="Feeding + tactics"
                badge={bestTechniques[0] || bestBaits[0] || 'Fishing approach'}
                isDarkMode={isDarkMode}
              >
                <div className="space-y-3">
                  {feedingFact ? <p className="text-[14px] leading-6" style={{ color: ui.textSecondary }}>{feedingFact}</p> : null}
                  <div className="grid gap-2 sm:grid-cols-2">
                    <InfoBlock title="Baits / forage" tone="bait" isDarkMode={isDarkMode}>
                      {(bestBaits.length ? bestBaits.slice(0, 4) : forageList.slice(0, 4)).join(' • ') || 'Match the dominant local forage.'}
                    </InfoBlock>
                    <InfoBlock title="Techniques" tone="tech" isDarkMode={isDarkMode}>
                      {bestTechniques.slice(0, 4).join(' • ') || tacticFact || 'Use a clean, habitat-matched presentation.'}
                    </InfoBlock>
                  </div>
                </div>
              </CompactSection>
            ) : null}

            {kind === 'species' ? (
              <CompactSection
                title="Seasons + timing"
                badge={knowledgeSeasonTags[0] || knowledgeActivityWindows[0] || bestTimes[0] || 'Timing profile'}
                isDarkMode={isDarkMode}
              >
                <div className="space-y-3 text-[14px] leading-6" style={{ color: ui.textSecondary }}>
                  {seasonalMovementFact ? <p>{seasonalMovementFact}</p> : null}
                  {timingFact ? <p>{timingFact}</p> : null}
                  {spawningFact ? <p>{spawningFact}</p> : null}
                  <div className="flex flex-wrap gap-2">
                    {[...knowledgeSeasonTags.slice(0, 4), ...knowledgeActivityWindows.slice(0, 4)].map((item) => (
                      <span key={item} className="rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.10em]" style={premiumChipStyle(isDarkMode, 'tech')}>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </CompactSection>
            ) : null}

            {referenceImages.length > 0 ? (
              <CompactSection
                title="Reference photos"
                badge={`${referenceImages.length} verified`}
                isDarkMode={isDarkMode}
              >
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {referenceImages.slice(0, 6).map((image) => (
                    <button
                      key={image.id}
                      type="button"
                      onClick={() => setExpandedReferenceImage(image)}
                      className="group relative overflow-hidden rounded-[14px] border text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70"
                      style={{ borderColor: ui.border }}
                      aria-label={`Open full-size reference image for ${displayName}`}
                    >
                      <FishIQSpeciesImage
                        species={entity}
                        image={image}
                        size="card"
                        className="aspect-[4/3] w-full"
                        imageClassName="object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                        isDarkMode={isDarkMode}
                      />
                      <div
                        className="absolute inset-x-0 bottom-0 px-2 py-1.5 text-[8px] font-black uppercase tracking-[0.08em]"
                        style={{
                          color: '#fff',
                          background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.78))',
                        }}
                      >
                        {String(image.angle || 'reference').replace(/_/g, ' ')}
                      </div>
                    </button>
                  ))}
                </div>
              </CompactSection>
            ) : null}

            <CompactSection
              title="Sources + harvest info"
              badge={showPublicSourceRegistry ? 'Source-backed' : 'Roddo profile'}
              isDarkMode={isDarkMode}
            >
              <div className="space-y-3 text-[14px] leading-6" style={{ color: ui.textSecondary }}>
                {showPublicSourceRegistry ? (
                  <div className="flex items-center justify-between gap-3 rounded-[14px] border px-3 py-3" style={{ borderColor: ui.border }}>
                    <span className="font-bold">Public source registry</span>
                    <FishIQSourceBadge species={entity} isDarkMode={isDarkMode} />
                  </div>
                ) : null}

                {confidenceParagraphs.length
                  ? confidenceParagraphs.map((paragraph, index) => <p key={`${paragraph}-${index}`}>{paragraph}</p>)
                  : <p>Roddo combines curated species biology, verified reference imagery, and local context when available.</p>}

                {kind === 'species' ? (
                  <div className="rounded-[14px] border px-3 py-3" style={{ borderColor: isDarkMode ? 'rgba(242,184,75,0.20)' : 'rgba(160,90,0,0.16)', background: isDarkMode ? 'rgba(242,184,75,0.06)' : 'rgba(255,248,221,0.80)' }}>
                    <div className="text-[9px] font-black uppercase tracking-[0.14em]" style={{ color: isDarkMode ? '#F2B84B' : '#A05A00' }}>
                      Harvest guardrail
                    </div>
                    <p className="mt-1 text-[12px] font-semibold leading-5">
                      {cautionText || 'Check current local seasons, limits, licenses, closures, and advisories before harvesting.'}
                    </p>
                    {harvestModes.length ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {harvestModes.map((mode) => (
                          <span key={mode} className="rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.10em]" style={premiumChipStyle(isDarkMode, 'bait')}>
                            {mode}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {knowledgeFieldNotes.length ? <BulletList items={knowledgeFieldNotes.slice(0, 4)} isDarkMode={isDarkMode} /> : null}
              </div>
            </CompactSection>
          </div>
        </div>
      </div>

      {expandedReferenceImage ? (
        <div
          className="roddo-safe-overlay fixed inset-0 z-[120] flex items-center justify-center bg-black/90 px-3 backdrop-blur-sm sm:px-6"
          role="dialog"
          aria-modal="true"
          aria-label={`${displayName} reference image`}
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) setExpandedReferenceImage(null);
          }}
        >
          <div className="roddo-safe-modal-frame relative flex w-full max-w-5xl flex-col overflow-hidden rounded-[20px] border border-white/15 bg-black/70 shadow-2xl">
            <button
              type="button"
              onClick={() => setExpandedReferenceImage(null)}
              className="absolute right-3 top-3 z-10 grid h-11 w-11 place-items-center rounded-full border border-white/20 bg-black/70 text-2xl font-bold text-white shadow-lg backdrop-blur-md active:scale-[0.98]"
              aria-label="Close full-size reference image"
            >
              ×
            </button>

            <div className="flex min-h-0 flex-1 items-center justify-center bg-black">
              <img
                src={expandedReferenceImage.heroUrl || expandedReferenceImage.url || expandedReferenceImage.cardUrl}
                alt={`${displayName} verified reference`}
                className="max-h-[calc(var(--roddo-visual-vh,100dvh)-120px)] w-full object-contain"
                decoding="async"
              />
            </div>

            <div className="border-t border-white/10 bg-black/85 px-4 py-3 text-white sm:px-5">
              <div className="pr-12 text-[13px] font-black">{displayName}</div>
              <div className="mt-1 text-[11px] leading-5 text-white/70">
                {expandedReferenceImage.creditText || expandedReferenceImage.sourceAgency || expandedReferenceImage.sourceName || 'Verified Roddo reference'}
              </div>
              {expandedReferenceImage.sourceUrl ? (
                <a
                  href={expandedReferenceImage.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block text-[11px] font-black text-sky-300 underline underline-offset-2"
                >
                  View source
                </a>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
    </>
  );
};

export default FishIQDetailView;
